// src/pages/EmailBlastsPage.js
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DataGrid from '../components/DataGrid'; // [marker: step1-templates-grids v1]
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import QuillResize from 'quill-resize-module';
import 'quill-resize-module/dist/resize.css';

// Preserve image width/height/style so drag-resize survives save → reload → send.
const _BaseImage = Quill.import('formats/image');
const _IMG_ATTRS = ['alt', 'height', 'width', 'style'];
class _ResizableImage extends _BaseImage {
  static formats(domNode) {
    return _IMG_ATTRS.reduce((acc, a) => {
      if (domNode.hasAttribute(a)) acc[a] = domNode.getAttribute(a);
      return acc;
    }, {});
  }
  format(name, value) {
    if (_IMG_ATTRS.indexOf(name) > -1) {
      if (value) this.domNode.setAttribute(name, value);
      else this.domNode.removeAttribute(name);
    } else {
      super.format(name, value);
    }
  }
}
Quill.register(_ResizableImage, true);
Quill.register('modules/resize', QuillResize);

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'image'],
    ['clean'],
  ],
  resize: {
    locale: {},
    modules: ['Resize', 'DisplaySize'],
  },
};
const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'align',
  'link', 'image', 'width', 'height',
];

export default function EmailBlastsPage() {
  const [blasts, setBlasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [editingBlast, setEditingBlast] = useState(null);
  const [viewingBlast, setViewingBlast] = useState(null); /* [marker: blast-detail-view v1] */

  const reloadBlasts = () => api.get('/email-blasts').then(r => setBlasts(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([api.get('/email-blasts'), api.get('/emails/status').catch(() => ({ data: { configured: true } }))])
      .then(([b, s]) => { setBlasts(b.data); setEmailStatus(s.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (blast) => {
    if (!window.confirm(`Delete draft blast "${blast.subject}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/email-blasts/${blast.id}`);
      toast.success('Draft deleted.');
      reloadBlasts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete.');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Email Blasts</h1>
          {emailStatus && !emailStatus.configured && (
            <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)', padding: '8px 12px', marginTop: 8, fontSize: '0.8rem', color: 'var(--warning)' }}>
              ⚠ Email is not configured. Add SMTP credentials to your .env file.
            </div>
          )}
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Blast</button>
      </div>

      <BounceScanBar />

      {/* [marker: step1-templates-grids v1] Excel-style list */}
      <DataGrid
        gridKey="email-blasts"
        rows={blasts}
        loading={loading}
        onRowClick={b => setViewingBlast(b)}
        height="calc(100vh - 300px)"
        emptyText="No email blasts yet."
        columns={[
          { key: 'subject', label: 'Subject', width: 300, render: b => <strong>{b.subject}</strong> },
          { key: 'target_type', label: 'Sent To', width: 150, text: b => (b.target_type || '').replace(/_/g, ' ') },
          { key: 'recipient_count', label: 'Recipients', width: 100, type: 'number' },
          { key: 'open_count', label: 'Opens', width: 80, type: 'number' },
          { key: 'open_rate', label: 'Open %', width: 80, type: 'number',
            get: b => (parseInt(b.recipient_count) > 0 ? Math.round(100 * (parseInt(b.open_count) || 0) / parseInt(b.recipient_count)) : null),
            text: b => (parseInt(b.recipient_count) > 0 ? Math.round(100 * (parseInt(b.open_count) || 0) / parseInt(b.recipient_count)) + '%' : '') },
          { key: 'status', label: 'Status', width: 160, text: b => b.status === 'scheduled' && b.scheduled_at ? `scheduled ${new Date(b.scheduled_at).toLocaleString()}` : b.status, /* [marker: event-emails v1] */
            render: b => <span><span className={`badge badge-${b.status}`}>{b.status}</span>{b.status === 'scheduled' && b.scheduled_at && <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)', marginLeft: 6 }}>{new Date(b.scheduled_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}</span> },
          { key: 'sent_at', label: 'Sent', width: 110, type: 'date' },
          { key: 'created_at', label: 'Created', width: 110, type: 'date', defaultHidden: true },
          { key: 'created_by', label: 'Created By', width: 130, defaultHidden: true, get: b => [b.created_by_first_name, b.created_by_last_name].filter(Boolean).join(' ') },
          { key: 'actions', label: '', width: 80, text: () => '',
            render: b => b.status === 'scheduled' ? ( /* [marker: event-emails v1] */
              <span onClick={e => e.stopPropagation()}>
                <button className="btn btn-ghost btn-sm" style={{ padding: '0 6px' }} title="Unschedule (back to draft)"
                  onClick={async () => { try { await api.post(`/email-blasts/${b.id}/unschedule`); toast.success('Back in drafts.'); reloadBlasts(); } catch (err) { toast.error(err.response?.data?.error || 'Could not unschedule.'); } }}>Unschedule</button>
              </span>
            ) : b.status === 'draft' ? (
              <span style={{ display: 'inline-flex', gap: 2 }} onClick={e => e.stopPropagation()}>
                <button className="btn btn-ghost btn-sm" style={{ padding: '0 6px' }} onClick={() => setEditingBlast(b)} title="Edit draft">✎</button>
                <button className="btn btn-ghost btn-sm" style={{ padding: '0 6px', color: 'var(--danger)' }} onClick={() => handleDelete(b)} title="Delete draft">✕</button>
              </span>
            ) : null },
        ]}
      />

      {showModal && <BlastModal mode="create" onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); reloadBlasts(); }} />}
      {editingBlast && <BlastModal mode="edit" blast={editingBlast} onClose={() => setEditingBlast(null)} onSaved={() => { setEditingBlast(null); reloadBlasts(); }} />}
      {viewingBlast && <BlastDetailModal blast={viewingBlast} onClose={() => setViewingBlast(null)} />}
    </div>
  );
}

// ─── BounceScanBar — [marker: email-bounce-detect v1] ────────────────────────
// Shows bounce-detection status and a "Check now" button.
function BounceScanBar() {
  const [st, setSt] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get('/email-blasts/bounce-scan/status').then(r => setSt(r.data)).catch(() => {});
  }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/email-blasts/bounce-scan/run');
      setSt(r.data.status);
      const res = r.data.result || {};
      if (res.error) toast.error('Bounce check failed — see message on the page.');
      else if (res.skipped) toast('Bounce check is already running.');
      else toast.success(`Bounce check done: ${res.contactsFlagged || 0} contact(s) newly flagged.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bounce check failed.');
    } finally {
      setRunning(false);
    }
  };

  if (!st) return null;
  const problem = st.status === 'needs_read_scope' || st.status === 'error' || st.status === 'not_configured';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      margin: '0 0 12px', padding: '8px 12px', borderRadius: 'var(--radius)', fontSize: '0.8rem',
      background: problem ? 'var(--warning-bg)' : 'var(--gray-50)',
      border: '1px solid ' + (problem ? 'var(--warning)' : 'var(--gray-200)'),
      color: problem ? 'var(--warning)' : 'var(--gray-600)',
    }}>
      <span>
        <strong>Bounce detection:</strong>{' '}
        {st.status === 'ok' && <>last checked {st.lastOkAt ? new Date(st.lastOkAt).toLocaleString() : '—'} · {st.flaggedContacts} contact{st.flaggedContacts === 1 ? '' : 's'} marked Email Error (excluded from blasts)</>}
        {st.status === 'pending' && <>first check runs a few minutes after deploy (includes past bounces).</>}
        {problem && <>{st.detail || st.status}</>}
        {st.status === 'ok' && st.detail && <> · {st.detail}</>}
      </span>
      <button type="button" className="btn btn-secondary btn-sm" onClick={runNow} disabled={running || st.running}>
        {running ? 'Checking…' : 'Check now'}
      </button>
    </div>
  );
}

// ─── BlastTemplateBar — start a newsletter from a template / save as one ──────
// [marker: step1-templates-grids v1]
function BlastTemplateBar({ subject, bodyHtml, onApply }) {
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get('/email-compose/templates').then(r => setTemplates(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const useTemplate = async (id) => {
    if (!id) return;
    const hasBody = String(bodyHtml || '').replace(/<[^>]*>/g, '').trim().length > 0;
    if (hasBody && !window.confirm('Replace the current message with this template?')) return;
    setBusy(true);
    try {
      const r = await api.get(`/email-templates/${id}`);
      const t = r.data || {};
      let body = t.body_html || '';
      if (t.signature_block) body += `<div class="signature">${t.signature_block}</div>`;
      if (t.footer_text) body += `<div class="footer">${t.footer_text}</div>`;
      onApply(t.subject || '', body);
      toast.success(/\{\{/.test(body)
        ? `Loaded "${t.template_name}". Note: {{placeholders}} are not filled in for blasts — edit them out.`
        : `Loaded "${t.template_name}".`);
    } catch (err) {
      toast.error('Could not load that template.');
    } finally { setBusy(false); }
  };

  const saveAsTemplate = async () => {
    if (!String(bodyHtml || '').replace(/<[^>]*>/g, '').trim() && !/<img/i.test(bodyHtml || '')) return toast.error('Write the message first.');
    const name = window.prompt('Name for this template (e.g. "Monthly Newsletter"):');
    if (!name || !name.trim()) return;
    setBusy(true);
    try {
      await api.post('/email-templates', { templateName: name.trim(), subject: (subject || name).trim(), bodyHtml });
      toast.success(`Saved as template "${name.trim()}".`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the template.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 6 }}>
      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-600)' }}>Template:</span>
      <select className="form-select" style={{ maxWidth: 300, height: 34 }} value="" disabled={busy} onChange={e => useTemplate(e.target.value)}>
        <option value="">— Start from a template… —</option>
        {templates.map(t => <option key={t.id} value={t.id}>{t.template_name}</option>)}
      </select>
      <button type="button" className="btn btn-secondary btn-sm" onClick={saveAsTemplate} disabled={busy}>💾 Save as template</button>
      <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Manage all templates under Email Templates.</span>
    </div>
  );
}

// ─── BlastModal (handles both create and edit) ───────────────────────────────
function BlastModal({ mode, blast, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState({
    subject:    '',
    bodyHtml:   '',
    targetType: 'all_members',
    targetMetadata: {},
    attachmentFilename: null,
    attachmentOriginalName: null,
    attachmentMimeType: null,
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);   // [marker: event-emails v1] send later
  const [schedAt, setSchedAt] = useState('');
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [testEmail, setTestEmail] = useState(''); /* patch:blast-test-state */
  const [testing, setTesting] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false); /* patch:blast-attach-state */

  // Picker options (loaded once)
  const [tags, setTags] = useState([]);
  const [events, setEvents] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [salespersons, setSalespersons] = useState([]);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const setMeta = (k, v) => setForm(f => ({...f, targetMetadata: { ...(f.targetMetadata || {}), [k]: v }}));

  useEffect(() => {
    // Load picker options
    api.get('/contact-tags').then(r => setTags(r.data.filter(t => t.is_active !== false))).catch(() => {});
    api.get('/events?limit=200').then(r => setEvents(r.data.data || r.data || [])).catch(() => {});
    api.get('/membership-types').then(r => setMembershipTypes(r.data || [])).catch(() => {});
    api.get('/lookups/salespersons').then(r => setSalespersons(r.data || [])).catch(() => {});

    if (isEdit && blast) {
      api.get(`/email-blasts/${blast.id}`)
        .then(res => {
          setForm({
            subject:        res.data.subject || '',
            bodyHtml:       res.data.body_html || '',
            targetType:     res.data.target_type || 'all_members',
            targetMetadata: res.data.target_metadata || {},
            attachmentFilename:     res.data.attachment_filename || null,
            attachmentOriginalName: res.data.attachment_original_name || null,
            attachmentMimeType:     res.data.attachment_mime_type || null,
          });
        })
        .catch(() => toast.error('Failed to load blast.'))
        .finally(() => setLoading(false));
    }
  }, [isEdit, blast]);

  // When target type changes, reset metadata to avoid stale fields
  const changeTargetType = (newType) => {
    setForm(f => ({ ...f, targetType: newType, targetMetadata: {} }));
    setPreview(null);
  };

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await api.post('/email-blasts/preview-recipients', {
        targetType:     form.targetType,
        targetMetadata: form.targetMetadata,
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not preview.');
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const validate = () => {
    if (!form.subject.trim()) { toast.error('Subject is required.'); return false; }
    if (!form.bodyHtml.trim()) { toast.error('Body is required.'); return false; }
    if (form.targetType === 'tag' && !form.targetMetadata?.tag_id) {
      toast.error('Pick a tag.'); return false;
    }
    if (form.targetType === 'membership_type' && !form.targetMetadata?.membership_type_id) {
      toast.error('Pick a membership type.'); return false;
    }
    if (form.targetType === 'event_attendees' && !form.targetMetadata?.event_id) {
      toast.error('Pick an event.'); return false;
    }
    if (form.targetType === 'salesperson' && !form.targetMetadata?.salesperson_id) {
      toast.error('Pick a salesperson.'); return false;
    }
    if (form.targetType === 'renewal_month' && !form.targetMetadata?.renewal_month) {
      toast.error('Pick a renewal month.'); return false;
    }
    if (form.targetType === 'manual_list' && !(form.targetMetadata?.manual_emails && form.targetMetadata.manual_emails.length)) {
      toast.error('Paste at least one email address.'); return false;
    }
    return true;
  };

  const handleSaveDraft = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/email-blasts/${blast.id}`, { ...form, attachmentProvided: true });
        toast.success('Draft updated.');
      } else {
        await api.post('/email-blasts', form);
        toast.success('Draft saved.');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed.');
    } finally { setSaving(false); }
  };

  const handleSendNow = async () => {
    if (!validate()) return;
    const recipientLine = preview ? `${preview.count} recipients` : 'all matched recipients';
    if (!window.confirm(`Send this blast to ${recipientLine} now?`)) return;
    setSending(true);
    try {
      let blastId;
      if (isEdit) {
        await api.patch(`/email-blasts/${blast.id}`, { ...form, attachmentProvided: true });
        blastId = blast.id;
      } else {
        const draftRes = await api.post('/email-blasts', form);
        blastId = draftRes.data.id;
      }
      await api.post(`/email-blasts/${blastId}/send`);
      toast.success('Blast sending!');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send.');
    } finally { setSending(false); }
  };

  // [marker: event-emails v1] save, then schedule for later
  const handleSchedule = async () => {
    if (!validate()) return;
    if (!schedAt) { toast.error('Pick the date and time to send it.'); return; }
    const when = new Date(schedAt);
    if (isNaN(when.getTime()) || when.getTime() < Date.now()) { toast.error('Pick a time in the future.'); return; }
    setSending(true);
    try {
      let blastId;
      if (isEdit) { await api.patch(`/email-blasts/${blast.id}`, { ...form, attachmentProvided: true }); blastId = blast.id; }
      else { const draftRes = await api.post('/email-blasts', form); blastId = draftRes.data.id; }
      await api.post(`/email-blasts/${blastId}/schedule`, { scheduledAt: when.toISOString() });
      toast.success(`Scheduled for ${when.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not schedule.');
    } finally { setSending(false); }
  };

  const handleSendTest = async () => {
    if (!form.subject.trim()) { toast.error('Add a subject first.'); return; }
    if (!form.bodyHtml.trim()) { toast.error('Add a message body first.'); return; }
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((testEmail || '').trim());
    if (!ok) { toast.error('Enter a valid test email address.'); return; }
    setTesting(true);
    try {
      await api.post('/email-blasts/send-test', {
        subject:   form.subject,
        bodyHtml:  form.bodyHtml,
        testEmail: testEmail.trim(),
        attachmentFilename:     form.attachmentFilename,
        attachmentOriginalName: form.attachmentOriginalName,
        attachmentMimeType:     form.attachmentMimeType,
      });
      toast.success('Test sent to ' + testEmail.trim() + '.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send test.');
    } finally { setTesting(false); }
  };

  const handleAttachmentChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const okType = file.type === 'application/pdf' || file.type === 'image/png';
    if (!okType) { toast.error('Only PDF or PNG files are allowed.'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File is too large (max 10 MB).'); return; }
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/email-blasts/upload-attachment', fd, { headers: { 'Content-Type': undefined } });
      setForm(f => ({ ...f,
        attachmentFilename:     res.data.filename,
        attachmentOriginalName: res.data.originalName,
        attachmentMimeType:     res.data.mimeType,
      }));
      toast.success('Attachment added.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed.');
    } finally { setUploadingAttachment(false); }
  };

  const handleRemoveAttachment = () => {
    setForm(f => ({ ...f, attachmentFilename: null, attachmentOriginalName: null, attachmentMimeType: null }));
  };


  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Draft Blast' : 'New Email Blast'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {loading ? <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div> : (
            <>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label className="form-label">Subject *</label>
                  <input className="form-input" value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Email subject line..." autoFocus={!isEdit} />
                </div>
                <div className="form-group">
                  <label className="form-label">Send To *</label>
                  <select className="form-select" value={form.targetType} onChange={e => changeTargetType(e.target.value)}>
                    <option value="all_members">All Active Members</option>
                    <option value="opt_in">All Opt-In Contacts</option>
                    <option value="tag">By Contact Tag</option>
                    <option value="membership_type">Specific Membership Type</option>
                    <option value="event_attendees">Event Attendees</option>
                    <option value="all_contacts">All Contacts (incl. non-members)</option>
                    {/* [marker: blast-non-vip-target v1] */}
                    <option value="non_vip_contacts">All Contacts — Excluding VIPs</option>
                    <option value="lapsed_grace">Lapsed / Grace-Period Members</option>
                    <option value="salesperson">By Salesperson</option>
                    <option value="renewal_month">By Renewal Month</option>
                    <option value="manual_list">Paste a List of Emails</option>
                  </select>
                </div>
              </div>

              {/* Conditional pickers */}
              {form.targetType === 'tag' && (
                <div className="form-group">
                  <label className="form-label">Choose Tag *</label>
                  <select
                    className="form-select"
                    value={form.targetMetadata?.tag_id || ''}
                    onChange={e => { setMeta('tag_id', parseInt(e.target.value, 10) || null); setPreview(null); }}
                  >
                    <option value="">— select a tag —</option>
                    {tags.map(t => (
                      <option key={t.id} value={t.id}>{t.name} ({t.contact_count || 0})</option>
                    ))}
                  </select>
                  <p className="form-hint">Sends to all contacts with this tag who haven't unsubscribed.</p>
                </div>
              )}

              {form.targetType === 'membership_type' && (
                <div className="form-group">
                  <label className="form-label">Choose Membership Type *</label>
                  <select
                    className="form-select"
                    value={form.targetMetadata?.membership_type_id || ''}
                    onChange={e => { setMeta('membership_type_id', parseInt(e.target.value, 10) || null); setPreview(null); }}
                  >
                    <option value="">— select a membership type —</option>
                    {membershipTypes.map(mt => (
                      <option key={mt.id} value={mt.id}>{mt.name || mt.type_name}</option>
                    ))}
                  </select>
                </div>
              )}

              {form.targetType === 'salesperson' && (
                <div className="form-group">
                  <label className="form-label">Choose Salesperson *</label>
                  <select
                    className="form-select"
                    value={form.targetMetadata?.salesperson_id || ''}
                    onChange={e => { setMeta('salesperson_id', parseInt(e.target.value, 10) || null); setPreview(null); }}
                  >
                    <option value="">— select a salesperson —</option>
                    {salespersons.map(sp => (
                      <option key={sp.id} value={sp.id}>{sp.full_name || sp.initials || sp.name}</option>
                    ))}
                  </select>
                  <p className="form-hint">Sends to opted-in contacts at businesses assigned to this salesperson.</p>
                </div>
              )}

              {form.targetType === 'renewal_month' && (
                <div className="form-group">
                  <label className="form-label">Renewal Month *</label>
                  <select
                    className="form-select"
                    value={form.targetMetadata?.renewal_month || ''}
                    onChange={e => { setMeta('renewal_month', e.target.value || null); setPreview(null); }}
                  >
                    <option value="">— select —</option>
                    <option value="January">January</option>
                    <option value="July">July</option>
                  </select>
                </div>
              )}

              {form.targetType === 'event_attendees' && (
                <div className="form-group">
                  <label className="form-label">Choose Event *</label>
                  <select
                    className="form-select"
                    value={form.targetMetadata?.event_id || ''}
                    onChange={e => { setMeta('event_id', parseInt(e.target.value, 10) || null); setPreview(null); }}
                  >
                    <option value="">— select an event —</option>
                    {events.map(ev => (
                      <option key={ev.id} value={ev.id}>{ev.name} {ev.event_date ? `(${new Date(ev.event_date).toLocaleDateString()})` : ''}</option>
                    ))}
                  </select>
                  <p className="form-hint">Sends to everyone who bought a paid or comp ticket for this event.</p>
                </div>
              )}

              {form.targetType === 'manual_list' && (
                <div className="form-group">
                  <label className="form-label">Paste Emails *</label>
                  <textarea
                    className="form-input"
                    rows={6}
                    value={form.targetMetadata?.manual_raw || ''}
                    onChange={e => {
                      const raw = e.target.value;
                      setForm(f => ({ ...f, targetMetadata: { ...(f.targetMetadata || {}), manual_raw: raw, manual_emails: parseEmailList(raw) } }));
                      setPreview(null);
                    }}
                    placeholder="Paste emails separated by commas, spaces, semicolons, or new lines…"
                    style={{ width: '100%', minHeight: 120, fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
                  />
                  <p className="form-hint">
                    {(form.targetMetadata?.manual_emails?.length || 0)} unique address{(form.targetMetadata?.manual_emails?.length === 1 ? '' : 'es')} detected. Duplicates are removed; invalid ones are skipped at send. These recipients get no unsubscribe link.
                  </p>
                </div>
              )}

              {/* Preview button */}
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handlePreview} disabled={previewing}>
                  {previewing ? 'Counting…' : '👁 Preview Recipients'}
                </button>
                {preview && (
                  <div style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>
                    <strong>{preview.count}</strong> recipients will receive this
                    {preview.skippedCount > 0 && (
                      <span style={{ color: 'var(--gray-400)', marginLeft: 8 }}>
                        ({preview.skippedCount} skipped: missing or invalid email)
                      </span>
                    )}
                    {preview.emailErrorExcluded > 0 && (
                      <span style={{ color: 'var(--gray-400)', marginLeft: 8 }}>
                        ({preview.emailErrorExcluded} excluded: Email Error / bounced)
                      </span>
                    )}
                    {preview.sample?.length > 0 && (
                      <details style={{ marginTop: 4 }}>
                        <summary style={{ cursor: 'pointer', fontSize: '0.78rem', color: 'var(--gray-500)' }}>Show sample</summary>
                        <div style={{ marginTop: 4, fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                          {preview.sample.slice(0, 10).map((r, i) => <div key={i}>{r.email}</div>)}
                          {preview.count > 10 && <div>…and {preview.count - 10} more</div>}
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </div>

              {/* Newsletter templates — [marker: step1-templates-grids v1] */}
              <BlastTemplateBar
                subject={form.subject}
                bodyHtml={form.bodyHtml}
                onApply={(subject, body) => setForm(f => ({ ...f, subject: subject || f.subject, bodyHtml: body }))}
              />

              <div className="form-group">
                <label className="form-label">Message Body *</label>
                <div style={{ background: 'white', borderRadius: 6 }}>
                  <ReactQuill
                    theme="snow"
                    value={form.bodyHtml}
                    onChange={value => set('bodyHtml', value)}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    placeholder="Write your message — use the toolbar for formatting, links, and images…"
                    style={{ minHeight: 280 }}
                  />
                </div>
                <p className="form-hint">
                  Use the toolbar for formatting. Images are embedded inline (base64). The organization header/footer is added automatically.
                </p>
              </div>

              {/* Attach a file (PDF or PNG) — patch:blast-attach-ui */}
              <div className="form-group">
                <label className="form-label">Attachment (PDF or PNG)</label>
                {form.attachmentFilename ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.875rem' }}>📎 {form.attachmentOriginalName || form.attachmentFilename}</span>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={handleRemoveAttachment} style={{ color: 'var(--danger)' }}>Remove</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="file" accept="application/pdf,image/png,.pdf,.png" onChange={handleAttachmentChange} disabled={uploadingAttachment} />
                    {uploadingAttachment && <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>Uploading…</span>}
                  </div>
                )}
                <p className="form-hint">Recipients receive this as a downloadable attachment. Max 10 MB. PDF or PNG only.</p>
              </div>

              {/* Send a test copy before blasting — patch:blast-test-ui */}
              <div className="form-group" style={{ borderTop: '1px solid var(--gray-200)', paddingTop: 16 }}>
                <label className="form-label">Send a Test First</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    className="form-input"
                    type="email"
                    value={testEmail}
                    onChange={e => setTestEmail(e.target.value)}
                    placeholder="you@example.com"
                    style={{ flex: 1, minWidth: 220 }}
                  />
                  <button type="button" className="btn btn-secondary btn-sm" onClick={handleSendTest} disabled={testing}>
                    {testing ? 'Sending test…' : '✉ Send Test'}
                  </button>
                </div>
                <p className="form-hint">Sends one copy of this message (with the org header/footer) to the address above so you can check how it looks before blasting. Real recipients are not affected.</p>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-secondary" onClick={handleSaveDraft} disabled={saving || sending}>
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Save Draft')}
          </button>
          {/* [marker: event-emails v1] send later */}
          {schedOpen ? (
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <input className="form-input" type="datetime-local" value={schedAt} onChange={e => setSchedAt(e.target.value)} style={{ width: 'auto' }} aria-label="Send on" />
              <button className="btn btn-primary" onClick={handleSchedule} disabled={saving || sending}>{sending ? 'Scheduling…' : 'Schedule'}</button>
            </span>
          ) : (
            <button className="btn btn-secondary" onClick={() => setSchedOpen(true)} disabled={saving || sending}>Send Later…</button>
          )}
          <button className="btn btn-primary" onClick={handleSendNow} disabled={saving || sending}>
            {sending ? 'Sending…' : 'Send Now'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── BlastDetailModal — view a sent blast: inbox render + recipients ─────────
function BlastDetailModal({ blast, onClose }) {
  const [detail, setDetail] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const [tab, setTab] = useState('email'); // 'email' | 'recipients'
  const [recipientSearch, setRecipientSearch] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/email-blasts/${blast.id}`)
      .then(r => { if (alive) setDetail(r.data); })
      .catch(() => { if (alive) toast.error('Could not load blast details.'); })
      .finally(() => { if (alive) setLoading(false); });
    setRecipientsLoading(true);
    api.get(`/email-blasts/${blast.id}/recipients`)
      .then(r => { if (alive) setRecipients(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { /* non-fatal */ })
      .finally(() => { if (alive) setRecipientsLoading(false); });
    return () => { alive = false; };
  }, [blast.id]);

  const stats = detail?.stats || {};
  const bodyHtml = detail?.body_html || '';

  const filteredRecipients = recipientSearch
    ? recipients.filter(r => {
        const q = recipientSearch.toLowerCase();
        const name = `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase();
        return (r.email || '').toLowerCase().includes(q) || name.includes(q);
      })
    : recipients;

  const statCell = (label, value, color) => (
    <div style={{ flex: 1, textAlign: 'center', padding: '8px 4px' }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || 'var(--gray-900)' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 760, width: '92%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem' }}>{blast.subject}</h2>
            <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginTop: 4 }}>
              <span className={`badge badge-${blast.status}`}>{blast.status}</span>
              {blast.sent_at ? ` · Sent ${fmtDate(blast.sent_at)}` : ''}
              {detail?.attachment_original_name ? ` · 📎 ${detail.attachment_original_name}` : ''}
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose} title="Close">✕</button>
        </div>

        {/* Stats bar */}
        <div style={{ display: 'flex', borderTop: '1px solid var(--gray-200)', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50, #f9fafb)' }}>
          {statCell('Recipients', stats.total_recipients ?? (blast.recipient_count || 0))}
          {statCell('Sent', stats.sent_count ?? '—', 'var(--gray-900)')}
          {statCell('Opened', stats.open_count ?? 0, '#15803d')}
          {statCell('Bounced', stats.bounce_count ?? 0, (stats.bounce_count > 0 ? 'var(--danger)' : 'var(--gray-900)'))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '10px 16px 0' }}>
          <button
            className={`btn ${tab === 'email' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab('email')}
            style={{ padding: '6px 14px' }}
          >Email</button>
          <button
            className={`btn ${tab === 'recipients' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab('recipients')}
            style={{ padding: '6px 14px' }}
          >Recipients ({recipients.length})</button>
        </div>

        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div>
          ) : tab === 'email' ? (
            <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, overflow: 'hidden' }}>
              {/* Render the exact HTML that was sent, sandboxed so its styles
                  can't leak into the admin UI (this is the inbox view). */}
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={bodyHtml || '<p style="font-family:sans-serif;color:#888;padding:24px">No content stored for this blast.</p>'}
                style={{ width: '100%', height: '48vh', border: 'none', background: '#fff' }}
              />
            </div>
          ) : (
            <div>
              <input
                className="form-input"
                placeholder="Search recipients by name or email…"
                value={recipientSearch}
                onChange={e => setRecipientSearch(e.target.value)}
                style={{ marginBottom: 12 }}
              />
              {recipientsLoading ? (
                <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner" /></div>
              ) : filteredRecipients.length === 0 ? (
                <p style={{ color: 'var(--gray-500)', textAlign: 'center', padding: 20 }}>
                  {recipients.length === 0 ? 'No recipient records for this blast.' : 'No matches.'}
                </p>
              ) : (
                <div style={{ maxHeight: '46vh', overflowY: 'auto' }}>
                  <table style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Recipient</th>
                        <th style={{ textAlign: 'left' }}>Email</th>
                        <th style={{ textAlign: 'left' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecipients.map(r => (
                        <tr key={r.id}>
                          <td style={{ fontSize: '0.85rem' }}>
                            {r.contact_id && (r.first_name || r.last_name)
                              ? `${r.first_name || ''} ${r.last_name || ''}`.trim()
                              : <span style={{ color: 'var(--gray-400)' }}>—</span>}
                          </td>
                          <td style={{ fontSize: '0.85rem' }}>{r.email}</td>
                          <td style={{ fontSize: '0.8rem' }}>
                            {r.bounced ? (
                              <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Bounced</span>
                            ) : r.opened_at ? (
                              <span style={{ color: '#15803d', fontWeight: 600 }}>Opened</span>
                            ) : r.sent_at ? (
                              <span style={{ color: 'var(--gray-600)' }}>Sent</span>
                            ) : (
                              <span style={{ color: 'var(--gray-400)' }}>Pending</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function parseEmailList(raw) {
  if (!raw) return [];
  const seen = new Set();
  const out = [];
  String(raw).split(/[\s,;]+/).forEach(part => {
    const e = part.trim().toLowerCase();
    if (e && !seen.has(e)) { seen.add(e); out.push(e); }
  });
  return out;
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
